import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { NotificationBell } from "@/components/NotificationBell";
import { NAV_MODULES, isModuleVisibleForUser } from "@/lib/constants";
import * as Icons from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import ChangePasswordModal from "@/components/ChangePasswordModal";

// Yol → localStorage flag eşleşmesi (rehberi tamamlanan modüller için ✓ rozeti)
const ONBOARDING_FLAGS = {
  "/purchasing-scan": "akb_purchasing_onboarded_v1",
};

export default function Sidebar({ collapsed, onToggle, isMobile = false }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const [cpOpen, setCpOpen] = useState(false);

  // İlk render'da localStorage'ı oku
  const [onboarded, setOnboarded] = useState(() => {
    const s = {};
    for (const [p, k] of Object.entries(ONBOARDING_FLAGS)) s[p] = !!localStorage.getItem(k);
    return s;
  });

  // Grup açılıp/kapanma state — aktif rotayı içeren grup otomatik açılır
  const location = useLocation();
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const raw = localStorage.getItem("akb_sidebar_groups");
      if (raw) return JSON.parse(raw);
    } catch {}
    return {};
  });
  useEffect(() => {
    try { localStorage.setItem("akb_sidebar_groups", JSON.stringify(openGroups)); } catch {}
  }, [openGroups]);
  // Aktif path bir grup içindeyse o grubu otomatik aç
  useEffect(() => {
    const active = NAV_MODULES.find(m => m.path === location.pathname);
    if (active?.group && !openGroups[active.group]) {
      setOpenGroups(s => ({ ...s, [active.group]: true }));
    }
  }, [location.pathname]); // eslint-disable-line

  // Rehber tamamlandığında güncelle
  useEffect(() => {
    const refresh = () => {
      const s = {};
      for (const [p, k] of Object.entries(ONBOARDING_FLAGS)) s[p] = !!localStorage.getItem(k);
      setOnboarded(s);
    };
    window.addEventListener("akb:onboarding-completed", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("akb:onboarding-completed", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  const visible = NAV_MODULES.filter(m => isModuleVisibleForUser(m, user));

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <aside
      data-testid="app-sidebar"
      className={`sidebar-brand text-foreground flex flex-col apple-ease transition-[width] duration-300 ${
        collapsed ? "w-16" : "w-64"
      } h-screen sticky top-0 overflow-hidden`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-4 border-b border-border/60">
        {!collapsed ? (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <img
              src="/logo.png"
              alt="AKB"
              className="h-8 w-auto dark:brightness-0 dark:invert"
              onError={(e) => e.target.style.display='none'}
            />
            <div className="flex flex-col min-w-0">
              <span className="font-semibold text-[15px] tracking-tight truncate">AKB Karavan</span>
              <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground truncate">International Co.</span>
            </div>
          </div>
        ) : (
          <img
            src="/logo.png"
            alt="AKB"
            className="h-6 w-auto mx-auto dark:brightness-0 dark:invert"
            onError={(e) => e.target.style.display='none'}
          />
        )}
        {!isMobile && (
          <button
            onClick={onToggle}
            data-testid="sidebar-toggle-btn"
            className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground apple-ease transition-colors flex-shrink-0"
            aria-label="Menü daralt"
          >
            <Icons.PanelLeft className="w-4 h-4" />
          </button>
        )}
      </div>

      <ScrollArea className="flex-1 py-2">
        <nav className="space-y-0.5 px-2">
          {renderGrouped(visible, { collapsed, onboarded, openGroups, setOpenGroups })}
        </nav>
      </ScrollArea>

      <div className="border-t border-border/60 p-3 space-y-2">
        {!collapsed && (
          <div className="px-2 py-1">
            <p className="text-[13px] font-medium truncate">{user?.name}</p>
            <p className="text-[11px] text-muted-foreground truncate">{user?.email}</p>
          </div>
        )}
        <div className="flex gap-1">
          <NotificationBell collapsed={collapsed} />
          <Button
            variant="ghost"
            size="sm"
            onClick={toggle}
            data-testid="theme-toggle-btn"
            className="flex-1 apple-ease"
            title="Tema"
          >
            {theme === "dark" ? <Icons.Sun className="w-4 h-4" /> : <Icons.Moon className="w-4 h-4" />}
            {!collapsed && <span className="ml-2 text-xs">Tema</span>}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCpOpen(true)}
            title="Şifremi Değiştir"
            data-testid="change-password-btn"
            className="flex-1 apple-ease"
          >
            <Icons.KeyRound className="w-4 h-4" />
            {!collapsed && <span className="ml-2 text-xs">Şifre</span>}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            data-testid="logout-btn"
            className="flex-1 apple-ease"
            title="Çıkış"
          >
            <Icons.LogOut className="w-4 h-4" />
            {!collapsed && <span className="ml-2 text-xs">Çıkış</span>}
          </Button>
        </div>
      </div>
      <ChangePasswordModal open={cpOpen} onClose={() => setCpOpen(false)} />
    </aside>
  );
}


// ============================================================
// Gruplu sidebar render'ı — grup'suz item'ları direkt basar,
// grup'lu olanları accordion başlığı altında toplar.
// ============================================================
function renderGrouped(items, { collapsed, onboarded, openGroups, setOpenGroups }) {
  const output = [];
  const seenGroups = new Set();
  for (const m of items) {
    if (!m.group) {
      output.push(<NavItem key={m.path} m={m} collapsed={collapsed} onboarded={onboarded} />);
      continue;
    }
    if (seenGroups.has(m.group)) continue;
    seenGroups.add(m.group);
    const groupItems = items.filter(x => x.group === m.group);
    const isOpen = !!openGroups[m.group];
    const HeaderIcon = Icons[groupItems[0].icon] || Icons.Folder;
    output.push(
      <div key={`grp-${m.group}`} className="pt-1">
        <button
          type="button"
          onClick={() => setOpenGroups(s => ({ ...s, [m.group]: !s[m.group] }))}
          data-testid={`nav-group-${m.group.toLowerCase().replace(/\s/g, "-")}-toggle`}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm apple-ease transition-all text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <HeaderIcon className="w-4 h-4 flex-shrink-0" />
          {!collapsed && (
            <>
              <span className="truncate flex-1 text-left font-semibold text-[13px] uppercase tracking-wide">
                {m.group}
              </span>
              <Icons.ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
            </>
          )}
        </button>
        {isOpen && !collapsed && (
          <div className="ml-3 mt-0.5 border-l border-border/60 pl-2 space-y-0.5">
            {groupItems.map(gi => (
              <NavItem key={gi.path} m={gi} collapsed={collapsed} onboarded={onboarded} nested />
            ))}
          </div>
        )}
        {isOpen && collapsed && (
          <div className="space-y-0.5">
            {groupItems.map(gi => (
              <NavItem key={gi.path} m={gi} collapsed={collapsed} onboarded={onboarded} />
            ))}
          </div>
        )}
      </div>
    );
  }
  return output;
}

function NavItem({ m, collapsed, onboarded, nested = false }) {
  const Icon = Icons[m.icon] || Icons.Circle;
  const done = onboarded[m.path];
  return (
    <NavLink
      to={m.path}
      end={m.path === "/" || m.path === "/service"}
      data-testid={`nav-${m.label.toLowerCase().replace(/\s/g, "-")}-link`}
      className={({ isActive }) =>
        `nav-link flex items-center gap-3 ${nested ? "px-2 py-1.5" : "px-3 py-2"} rounded-xl text-sm apple-ease transition-all ${
          isActive
            ? "bg-primary/10 text-primary font-medium"
            : "text-muted-foreground hover:bg-muted hover:text-foreground"
        }`
      }
    >
      <span className="relative flex-shrink-0">
        <Icon className={nested ? "w-3.5 h-3.5" : "w-4 h-4"} />
        {done && (
          <span
            title="Rehberi tamamladınız"
            className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center w-3 h-3 rounded-full bg-emerald-500 ring-2 ring-[var(--sidebar-bg,white)] dark:ring-slate-950 shadow-sm"
          >
            <Icons.Check className="w-2 h-2 text-white" strokeWidth={4} />
          </span>
        )}
      </span>
      {!collapsed && <span className={`truncate ${nested ? "text-[13px]" : ""}`}>{m.label}</span>}
    </NavLink>
  );
}
